import type {
  StandingMilestone,
  StandingMilestoneKind,
  StandingMilestoneState,
  TeamStanding,
} from '@sportninja/common/features/team-balance/utils/teamStanding'
import type { TeamPaymentStatusCode } from '@sportninja/common/services/teamBalance'
import type { HeaderMoney } from './balanceTax'

/**
 * SN-8310 — the words the Team Budget header says, chosen from the standing
 * model and the server's money. Kept out of the components so the choice of
 * sentence is testable without rendering, and so the banner, the milestones
 * and the legend all phrase one date the same way.
 *
 * Inon, Sep 24: each fact is said ONCE. The banner's one sentence is the
 * headline — how much, by when, what for — and names a date only while it is
 * still ahead. The milestones carry the dates and the money still in play on
 * each; the figures row carries the amounts; the chip carries the clock.
 *
 * The formatters are passed in: `t` is the i18n lookup, `money` formats minor
 * units in the registration's currency, and `day` prints a competition day.
 */
export interface StandingFormatters {
  t: (key: string, params?: Record<string, unknown>) => string
  money: (minor: number) => string
  day: (day: string) => string
}

/** One payment under a date, coloured by its own state. */
export interface MilestoneLine {
  text: string
  state: StandingMilestoneState
}

export interface MilestoneCopy {
  title: string
  date: string
  lines: MilestoneLine[]
}

/** The chip under the pill: the clock on the deadline in play. */
export interface ClockCopy {
  text: string
  overdue: boolean
}

const minimumShortfall = (money: HeaderMoney): number =>
  Math.max(0, (money.minimum ?? 0) - money.paid)

const owed = (money: HeaderMoney): number => Math.max(0, money.outstanding)

/**
 * When the full balance is due, phrased by WHICH date drives it. The final
 * cutoff is the season start unless registration outlasts it, and the two bite
 * at opposite ends of their day: "before the season starts Sat, Oct 3" but "by
 * Fri, Oct 9, when registration closes".
 */
const finalWhen = (standing: TeamStanding, fmt: StandingFormatters): string =>
  fmt.t(
    standing.final.source === 'competition_start'
      ? 'common:balanceStandingBeforeSeason'
      : 'common:balanceStandingByRegistrationEnd',
    { date: fmt.day(standing.final.day) }
  )

/** "5 days left" / "Last day" while the deadline is ahead, else null. */
const countdownCopy = (
  standing: TeamStanding | null,
  fmt: StandingFormatters
): string | null => {
  const left = standing?.deadline?.daysLeft
  if (left === null || left === undefined) {
    return null
  }

  return left <= 1
    ? fmt.t('common:balanceStandingLastDay')
    : fmt.t('common:balanceStandingDaysLeft', { count: left })
}

/** "2 days overdue" / "Overdue since today" once the deadline passed, else null. */
const overdueCopy = (
  standing: TeamStanding | null,
  fmt: StandingFormatters
): string | null => {
  const days = standing?.deadline?.daysOverdue
  if (days === null || days === undefined) {
    return null
  }

  return days > 0
    ? fmt.t('common:balanceStandingDaysOverdue', { count: days })
    : fmt.t('common:balanceStandingOverdueToday')
}

/**
 * The chip under the pill (Inon, Sep 24): days left while the money is due,
 * days overdue once its deadline is missed — Not Qualified and Balance Overdue
 * alike — and nothing once the team is paid in full. The header's only clock.
 */
export const clockCopy = (
  standing: TeamStanding | null,
  fmt: StandingFormatters
): ClockCopy | null => {
  const left = countdownCopy(standing, fmt)
  if (left) {
    return { text: left, overdue: false }
  }
  const late = overdueCopy(standing, fmt)

  return late ? { text: late, overdue: true } : null
}

/**
 * The banner's sentence.
 *
 * Every figure is the server's (`HeaderMoney`) and every date is the server's
 * `on` day, so nothing here can drift from the pill beside it. A date already
 * behind the team is left to the milestones, and the overdue count to the chip.
 */
export const standingSentence = (
  standing: TeamStanding,
  money: HeaderMoney,
  league: string | null,
  fmt: StandingFormatters
): string => {
  const m = fmt.money

  switch (standing.code) {
    case 'minimum_payment_due':
      return fmt.t(
        league
          ? 'common:balanceStandingMinimumDue'
          : 'common:balanceStandingMinimumDueNoLeague',
        {
          amount: m(minimumShortfall(money)),
          date: fmt.day(standing.qualification.day),
          league,
        }
      )
    case 'qualified_balance_due':
      return fmt.t('common:balanceStandingSecured', {
        amount: m(owed(money)),
        when: finalWhen(standing, fmt),
      })
    case 'not_qualified':
      return fmt.t('common:balanceStandingNotQualified', {
        minimum: m(money.minimum ?? 0),
        amount: m(minimumShortfall(money)),
      })
    case 'balance_overdue':
      return fmt.t('common:balanceStandingOverdue', { amount: m(owed(money)) })
    default:
      return money.outstanding < 0
        ? fmt.t('common:balanceStandingCredit', {
            amount: m(-money.outstanding),
          })
        : fmt.t('common:balanceStandingPaidInFull')
  }
}

/** Each date's title, before it and once it has passed. */
const STEP_TITLES: Record<
  StandingMilestoneKind,
  { upcoming: string; passed: string }
> = {
  registration_opens: {
    upcoming: 'common:balanceStandingStepOpens',
    passed: 'common:balanceStandingStepOpened',
  },
  registration_ends: {
    upcoming: 'common:balanceStandingStepEnds',
    passed: 'common:balanceStandingStepEnded',
  },
  competition_starts: {
    upcoming: 'common:balanceStandingStepSeasonStarts',
    passed: 'common:balanceStandingStepSeasonStarted',
  },
}

/**
 * One milestone's words: the date's name and day, then each payment still in
 * play on it (Inon, Sep 24 — a mark is a date; the money hangs off the date
 * it's due). Money that is in says nothing: its mark's green check does.
 * Progress figures stay in the money row and the shortfall in the sentence.
 */
export const milestoneCopy = (
  milestone: StandingMilestone,
  standing: TeamStanding,
  money: HeaderMoney,
  fmt: StandingFormatters
): MilestoneCopy => {
  const titles = STEP_TITLES[milestone.kind]
  const lines: MilestoneLine[] = []
  if (
    milestone.obligations.includes('minimum') &&
    standing.minimumState !== 'done'
  ) {
    lines.push({
      text: fmt.t('common:balanceStandingLineMinimum', {
        amount: fmt.money(money.minimum ?? 0),
      }),
      state: standing.minimumState,
    })
  }
  if (
    milestone.obligations.includes('balance') &&
    standing.balanceState !== 'done'
  ) {
    lines.push({
      text: fmt.t(
        standing.balanceState === 'missed'
          ? 'common:balanceStandingLineBalanceOverdue'
          : 'common:balanceStandingLineBalanceDue'
      ),
      state: standing.balanceState,
    })
  }

  return {
    title: fmt.t(milestone.passed ? titles.passed : titles.upcoming),
    date: fmt.day(milestone.day),
    lines,
  }
}

/** The five statuses in the order a team moves through them. */
export const LEGEND_ORDER: TeamPaymentStatusCode[] = [
  'minimum_payment_due',
  'not_qualified',
  'qualified_balance_due',
  'balance_overdue',
  'paid_in_full',
]

const LEGEND_KEYS: Record<TeamPaymentStatusCode, string> = {
  minimum_payment_due: 'common:teamPaymentStatusLegendMinimumPaymentDue',
  not_qualified: 'common:teamPaymentStatusLegendNotQualified',
  qualified_balance_due: 'common:teamPaymentStatusLegendQualifiedBalanceDue',
  balance_overdue: 'common:teamPaymentStatusLegendBalanceOverdue',
  paid_in_full: 'common:teamPaymentStatusLegendPaidInFull',
}

/** What each status means for THIS team, with its own amounts and dates. */
export const legendMeanings = (
  standing: TeamStanding,
  money: HeaderMoney,
  fmt: StandingFormatters
): Record<TeamPaymentStatusCode, string> => {
  const params = {
    minimum: fmt.money(money.minimum ?? 0),
    date: fmt.day(standing.qualification.day),
    total: fmt.money(money.amountDue),
    when: finalWhen(standing, fmt),
  }

  return LEGEND_ORDER.reduce(
    (meanings, code) => ({
      ...meanings,
      [code]: fmt.t(LEGEND_KEYS[code], params),
    }),
    {} as Record<TeamPaymentStatusCode, string>
  )
}
